import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface ChatMessage {
  id: string;
  content: string;
  senderType: 'visitor' | 'customer' | 'agent' | 'bot' | 'system';
  senderName?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

interface CustomerChatWebSocketOptions {
  tenantId: string;
  visitorId: string | null;
  sessionId: string | null;
  onMessage?: (message: ChatMessage) => void;
  onTyping?: (data: { userId: string; userType: string; isTyping: boolean }) => void;
  onAgentAssigned?: (data: { agentId: string; agentName?: string }) => void;
  onSessionEnded?: (data: { reason?: string }) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

class CustomerChatWebSocketService {
  private socket: Socket | null = null;
  private currentSessionId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  connect(tenantId: string, visitorId: string): Socket | null {
    if (this.socket?.connected) {
      return this.socket;
    }

    // Connect to channel-gateway chat namespace
    const wsUrl = import.meta.env.VITE_CHANNEL_WS_URL || 'https://dev-channel-gateway.centraone.ai';

    console.log('[CustomerChatWS] Connecting to:', wsUrl, 'tenant:', tenantId, 'visitor:', visitorId);

    this.socket = io(`${wsUrl}/chat`, {
      auth: {
        tenantId,
        visitorId,
        userType: 'visitor',
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      console.log('[CustomerChatWS] Connected to chat gateway');
      this.reconnectAttempts = 0;

      // Rejoin session if we were in one
      if (this.currentSessionId) {
        this.joinSession(this.currentSessionId);
      }
    });

    this.socket.on('connected', (data) => {
      console.log('[CustomerChatWS] Received connected event:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[CustomerChatWS] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[CustomerChatWS] Connection error:', error.message);
      this.reconnectAttempts++;
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentSessionId = null;
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  joinSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        console.warn('[CustomerChatWS] Cannot join session - not connected');
        resolve({ success: false, error: 'Not connected' });
        return;
      }

      console.log('[CustomerChatWS] Joining session:', sessionId);
      this.currentSessionId = sessionId;

      this.socket.emit('join_session', { sessionId }, (response: { success: boolean; error?: string }) => {
        console.log('[CustomerChatWS] Join session response:', response);
        resolve(response || { success: true });
      });
    });
  }

  leaveSession(sessionId: string) {
    if (this.socket?.connected) {
      console.log('[CustomerChatWS] Leaving session:', sessionId);
      this.socket.emit('leave_session', { sessionId });
      if (this.currentSessionId === sessionId) {
        this.currentSessionId = null;
      }
    }
  }

  sendMessage(sessionId: string, content: string, type = 'text') {
    if (this.socket?.connected) {
      console.log('[CustomerChatWS] Sending message to session:', sessionId);
      this.socket.emit('send_message', { sessionId, content, type });
    }
  }

  sendTyping(sessionId: string, isTyping: boolean) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { sessionId, isTyping });
    }
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }
}

// Singleton instance
const customerChatWebSocketService = new CustomerChatWebSocketService();

// React hook for customer chat WebSocket
export function useCustomerChatWebSocket(options: CustomerChatWebSocketOptions) {
  const {
    tenantId,
    visitorId,
    sessionId,
  } = options;

  const [isConnected, setIsConnected] = useState(customerChatWebSocketService.isConnected());
  const [hasJoinedSession, setHasJoinedSession] = useState(false);
  const optionsRef = useRef(options);

  // Update options ref without triggering reconnect
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Connect when visitorId is available
  useEffect(() => {
    if (!visitorId || !tenantId) {
      return;
    }

    // Check if already connected
    if (customerChatWebSocketService.isConnected()) {
      setIsConnected(true);
      return;
    }

    console.log('[CustomerChatWS] Initializing connection for visitor:', visitorId);
    const socket = customerChatWebSocketService.connect(tenantId, visitorId);

    if (socket) {
      const handleConnect = () => {
        console.log('[CustomerChatWS] Connected!');
        setIsConnected(true);
        optionsRef.current.onConnected?.();
      };

      const handleDisconnect = () => {
        console.log('[CustomerChatWS] Disconnected');
        setIsConnected(false);
        setHasJoinedSession(false);
        optionsRef.current.onDisconnected?.();
      };

      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);

      // Subscribe to events
      socket.on('new_message', (data: { sessionId: string; message: ChatMessage }) => {
        console.log('[CustomerChatWS] New message received:', data);
        // Socket.IO already scopes this event to the session room the widget
        // joined, so any new_message we receive is for the current conversation.
        // The previous `data.sessionId === sessionId` gate silently DROPPED agent
        // replies whenever the broadcast's id didn't exactly match the widget's
        // local sessionId (session-vs-conversation id differences), which is why
        // agent messages only appeared after a manual refresh (#9683). Trust the
        // room delivery here; ChatWidget's handler dedupes and ignores echoes of
        // the visitor's own messages.
        if (data?.message?.id) {
          optionsRef.current.onMessage?.(data.message);
        } else {
          console.warn('[CustomerChatWS] Received invalid message data:', data);
        }
      });

      socket.on('typing', (data: { sessionId: string; userId: string; userType: string; isTyping: boolean }) => {
        if (data?.sessionId === optionsRef.current.sessionId && data?.userId) {
          optionsRef.current.onTyping?.(data);
        }
      });

      socket.on('agent_assigned', (data: { sessionId: string; agentId: string; agentName?: string }) => {
        console.log('[CustomerChatWS] Agent assigned:', data);
        if (data?.sessionId === optionsRef.current.sessionId && data?.agentId) {
          optionsRef.current.onAgentAssigned?.(data);
        }
      });

      socket.on('session_ended', (data: { sessionId: string; reason?: string }) => {
        console.log('[CustomerChatWS] Session ended:', data);
        if (data?.sessionId === optionsRef.current.sessionId) {
          optionsRef.current.onSessionEnded?.(data);
        }
      });

      socket.on('user_joined', (data: { sessionId: string; userId: string; userType: string }) => {
        console.log('[CustomerChatWS] User joined:', data);
      });

      // If already connected, trigger handler
      if (socket.connected) {
        handleConnect();
      }
    }

    // Cleanup on unmount
    return () => {
      // Don't disconnect - keep connection alive for the session
    };
  }, [visitorId, tenantId]);

  // Join session when sessionId changes and we're connected
  useEffect(() => {
    if (!isConnected || !sessionId) {
      return;
    }

    // Check if already in this session
    if (customerChatWebSocketService.getCurrentSessionId() === sessionId) {
      setHasJoinedSession(true);
      return;
    }

    customerChatWebSocketService.joinSession(sessionId).then((result) => {
      if (result.success) {
        setHasJoinedSession(true);
      } else {
        console.error('[CustomerChatWS] Failed to join session:', result.error);
      }
    });
  }, [isConnected, sessionId]);

  const sendMessage = useCallback((content: string) => {
    if (sessionId) {
      customerChatWebSocketService.sendMessage(sessionId, content);
    }
  }, [sessionId]);

  const sendTyping = useCallback((isTyping: boolean) => {
    if (sessionId) {
      customerChatWebSocketService.sendTyping(sessionId, isTyping);
    }
  }, [sessionId]);

  const disconnect = useCallback(() => {
    customerChatWebSocketService.disconnect();
    setIsConnected(false);
    setHasJoinedSession(false);
  }, []);

  // CRITICAL FIX: Allow explicit session rejoin for error recovery scenarios
  // When a new session is created after error, WebSocket needs to leave old session and join new one
  const rejoinSession = useCallback(async (newSessionId: string): Promise<boolean> => {
    if (!isConnected) {
      console.warn('[CustomerChatWS] Cannot rejoin session - not connected');
      return false;
    }

    // Leave current session if different
    const currentSession = customerChatWebSocketService.getCurrentSessionId();
    if (currentSession && currentSession !== newSessionId) {
      console.log('[CustomerChatWS] Leaving old session for recovery:', currentSession);
      customerChatWebSocketService.leaveSession(currentSession);
    }

    // Join new session
    console.log('[CustomerChatWS] Rejoining new session:', newSessionId);
    const result = await customerChatWebSocketService.joinSession(newSessionId);
    if (result.success) {
      setHasJoinedSession(true);
      return true;
    } else {
      console.error('[CustomerChatWS] Failed to rejoin session:', result.error);
      setHasJoinedSession(false);
      return false;
    }
  }, [isConnected]);

  return {
    isConnected,
    hasJoinedSession,
    sendMessage,
    sendTyping,
    disconnect,
    rejoinSession,
  };
}

export { customerChatWebSocketService };
